/**
 * VERİTABANINDA SAKLANAN HATA METNİ — kod + ayrıntı (29 Eyl 2026, 13 dil).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Sorun
 * ═══════════════════════════════════════════════════════════════════════════
 * Bağlantı satırlarının `lastError` alanı (reklam, mağaza, mesajlaşma,
 * bildirim kanalı) serbest metin ve iki kaynaktan dolu: platformun kendi
 * hata cümlesi (çevrilemez, olduğu gibi gösterilmeli) ve BİZİM yazdığımız
 * Türkçe cümleler ("Kelime senkronu: …", "Uygulama mağazadan kaldırıldı…").
 * İkincisi Almanca panelde Türkçe uyarı demekti.
 *
 * ---------------------------------------------------------------------------
 * Biçim: `[KOD] ayrıntı`, birden çok parça ` · ` ile
 * ---------------------------------------------------------------------------
 * Sunucu cümle değil KOD yazıyor; ayrıntı (varsa platformun ham metni)
 * yanında. Ekran kodu kendi dilinde çeviriyor (`storedError.<KOD>`, web'de
 * `useStoredErrorText`), ayrıntıyı olduğu gibi basıyor. Kodsuz metin
 * (platformun kendi mesajı) değişmeden gösteriliyor.
 *
 * Şema değişmedi: alan yine düz metin. Kolon eklemek dört tabloya migration
 * ve her okuyana iki alan demekti; bu biçim eski satırlarla da uyumlu
 * (kodsuz eski metin olduğu gibi basılır).
 */
export const STORED_ERROR_CODES = [
  /** Google kelime adımı düştü — ayrıntı platformun cümlesi. */
  'KEYWORD_SYNC',
  /** Ürün metrikleri adımı düştü. */
  'PRODUCT_METRICS',
  /** Rakip açık artırma verisi adımı düştü. */
  'AUCTION_INSIGHTS',
  /** Mağaza uygulaması platformdan kaldırıldı; yeniden bağlanmak gerekiyor. */
  'STORE_APP_UNINSTALLED',
  /** Bağlantıyı platform yöneticisi kesti — ayrıntı yöneticinin yazdığı sebep. */
  'REVOKED_BY_ADMIN',
  /** Mesajlaşma oturumu açılamadı; QR yeniden okutulmalı. */
  'MESSAGING_SESSION_FAILED',
  /** Sağlayıcı hata metni vermedi. */
  'UNKNOWN_ERROR',

  // ── AI operatörü koşusu (`operator_runs.error`, Faz 5 — 29 Eyl 2026) ─────
  // Ayrıntı makine değeri ya da sanitize edilmiş sağlayıcı cümlesi; çevrilen
  // yalnız KOD. Eski satırlar "KOD: Türkçe ayrıntı" — kodsuz parça gibi basılır.
  /** Atlandı: abonelik yok/süresi dolmuş. */
  'PLAN_INACTIVE',
  /** Atlandı: operatör kapalı. */
  'OPERATOR_DISABLED',
  /** Atlandı: görev koşu kuyruktayken iptal edildi. */
  'TASK_NOT_RUNNABLE',
  /** Atlandı/ertelendi: planın günlük koşu tavanı doldu — ayrıntı: yeni vade (ISO). */
  'DAILY_RUN_LIMIT',
  /** Başsız döngü gövdesi yok (geçiş dönemi). */
  'OPERATOR_HEADLESS_NOT_IMPLEMENTED',
  /** Döngü süre tavanında kesildi — ayrıntı: dakika. */
  'OPERATOR_RUN_TIMEOUT',
  /** Koşu yarıda kaldı (API yeniden başladı) — ayrıntı: dakika. */
  'OPERATOR_RUN_INTERRUPTED',
  /** Kuyruğa atıldı ama hiç başlamadı. */
  'OPERATOR_RUN_LOST',
  /** Model hata döndü — ayrıntı: sağlayıcı hata kodu. */
  'OPERATOR_RUN_FAILED',
  /** Koşu iptal edildi. */
  'OPERATOR_RUN_ABORTED',
  /** Döngü beklenmedik biçimde çöktü — ayrıntı: sanitize edilmiş hata. */
  'OPERATOR_RUN_CRASHED',
  /** Model yapılandırılmamış (API anahtarı yok). */
  'LLM_NOT_CONFIGURED',
  /** Görevi taşıyan canlı koşu kalmadı (görev hatası). */
  'OPERATOR_TASK_ORPHANED',
  /** Diğer koşu hatası — ayrıntı: kod + sanitize edilmiş mesaj. */
  'OPERATOR_RUN_ERROR',
] as const;

export type StoredErrorCode = (typeof STORED_ERROR_CODES)[number];

/** Saklanacak metni kurar: `[KOD]` ya da `[KOD] ayrıntı`. */
export function storedError(code: StoredErrorCode, detail?: string | null): string {
  const text = detail?.trim();
  return text ? `[${code}] ${text}` : `[${code}]`;
}

export interface StoredErrorPart {
  /** `null` = kodsuz metin (platformun kendi cümlesi ya da eski kayıt). */
  code: StoredErrorCode | null;
  detail: string;
}

/** Saklanan metni parçalarına ayırır. Tanınmayan kod düz metin sayılır. */
export function parseStoredError(text: string): StoredErrorPart[] {
  return text
    .split(' · ')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^\[([A-Z][A-Z0-9_]*)\](?:\s+([\s\S]*))?$/);
      if (match && (STORED_ERROR_CODES as readonly string[]).includes(match[1] ?? '')) {
        return { code: match[1] as StoredErrorCode, detail: match[2]?.trim() ?? '' };
      }
      return { code: null, detail: part };
    });
}
